import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { WinAnimation } from '../components/WinAnimation';
import './CasinoSlots.css';

const SYMBOLS = ['7', 'BAR', '🍒', '🍋', '🍇'] as const;
type Symbol = typeof SYMBOLS[number];

const PAYOUTS: Record<Symbol, { three: number; two?: number }> = {
  '7': { three: 50, two: 5 },
  'BAR': { three: 25, two: 3 },
  '🍒': { three: 10, two: 2 },
  '🍋': { three: 5 },
  '🍇': { three: 3 },
};

function getRandomSymbol(): Symbol {
  return SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
}

function calculateWin(reels: [Symbol, Symbol, Symbol], bet: number): number {
  const [a, b, c] = reels;
  if (a === b && b === c) {
    return bet * (PAYOUTS[a].three ?? 0);
  }
  if (a === b || b === c || a === c) {
    const same = a === b ? a : b === c ? b : a;
    const payout = PAYOUTS[same].two ?? 0;
    return bet * payout;
  }
  return 0;
}

export function CasinoSlots() {
  const { gameState, placeBet, setLastWin, recordSpin, addBalance, betOptions } = useGame();
  const [reels, setReels] = useState<[Symbol, Symbol, Symbol]>(['🍒', '🍒', '🍒']);
  const [spinning, setSpinning] = useState(false);
  const [message, setMessage] = useState('');
  const [lastWin, setLastWinLocal] = useState(0);
  const [showWinAnim, setShowWinAnim] = useState(false);
  const [showBonusAnim, setShowBonusAnim] = useState(false);

  const canSpin = gameState.balance >= gameState.bet && !spinning;

  const spin = useCallback(() => {
    if (!canSpin) return;
    const bet = gameState.bet;
    addBalance(-bet);
    setSpinning(true);
    setMessage('');
    setLastWinLocal(0);

    const duration = 2000;
    const start = Date.now();
    const interval = setInterval(() => {
      setReels([getRandomSymbol(), getRandomSymbol(), getRandomSymbol()]);
      if (Date.now() - start > duration) {
        clearInterval(interval);
        const final: [Symbol, Symbol, Symbol] = [
          getRandomSymbol(),
          getRandomSymbol(),
          getRandomSymbol(),
        ];
        setReels(final);
        const win = calculateWin(final, bet);
        setLastWinLocal(win);
        recordSpin(win > 0);
        if (win > 0) {
          setLastWin(win);
          const isBonus = win >= bet * 10;
          setMessage(isBonus ? '🎉 BONUS VOITTO! 🎉' : 'Voitto!');
          setShowWinAnim(true);
          if (isBonus) setShowBonusAnim(true);
        } else {
          setMessage('Ei voittoa');
        }
        setSpinning(false);
      }
    }, 100);
  }, [canSpin, gameState.bet, setLastWin, recordSpin, addBalance]);

  return (
    <div className="casino-slots">
      {showWinAnim && lastWin > 0 && (
        <WinAnimation
          amount={lastWin}
          isBonus={showBonusAnim}
          onEnd={() => {
            setShowWinAnim(false);
            setShowBonusAnim(false);
          }}
        />
      )}
      <div className="casino-slots-container">
        <Link to="/" className="casino-back">← Takaisin</Link>

        <div className="casino-balance-bar">
          <div className="balance-display">
            <span className="balance-label">Saldo</span>
            <span className="balance-value">€{gameState.balance}</span>
          </div>
          <div className="bet-selector">
            <span className="bet-label">Panos:</span>
            <select
              value={gameState.bet}
              onChange={(e) => placeBet(Number(e.target.value))}
              disabled={spinning}
              className="bet-select"
            >
              {betOptions.map((b) => (
                <option key={b} value={b}>€{Number(b) === b && b % 1 !== 0 ? b.toFixed(1) : b}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="slot-machine">
          <div className="slot-title">LUCKY 7</div>
          <div className={`reels-container ${spinning ? 'spinning' : ''}`}>
            {reels.map((sym, i) => (
              <div key={i} className="reel">
                <div className="reel-symbol">{sym}</div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="spin-button"
            onClick={spin}
            disabled={!canSpin}
          >
            {spinning ? 'PYÖRII...' : `SPIN (€${gameState.bet})`}
          </button>
          {message && (
            <div className={`slot-message ${lastWin > 0 ? 'win' : 'lose'}`}>
              {message} {lastWin > 0 && `+€${lastWin}`}
            </div>
          )}
        </div>

        <div className="paytable">
          <h3>Voittotaulukko (× panos)</h3>
          <ul>
            <li>777 → 50×</li>
            <li>BAR BAR BAR → 25×</li>
            <li>🍒🍒🍒 → 10×</li>
            <li>🍋🍋🍋 → 5×</li>
            <li>🍇🍇🍇 → 3×</li>
            <li>Kaksi samanlaista → 2–5×</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
